/**
 * localize — the two write-intent channels behind the Languages action on an
 * event: `localizeCopyAction$` asks the model for a draft of the whole
 * event's copy in every UI language, and `acceptCopyAction$` stores the draft
 * the operator accepted. `localize.sync.ts` (web-side) is the sole subscriber
 * of both, reconciling each back by requestId — the shape
 * `legal/format/format.state.ts` already carries for the legal editor's own
 * format-and-translate action.
 *
 * The two channels are separate because the draft is not a write: nothing the
 * callable returns reaches Firestore until the operator has read every pane,
 * corrected whatever they want to correct, and pressed Accept. A run they
 * abandon costs one paid call and stores nothing.
 *
 * ONE ACCEPT, ONE BATCH. `acceptCopyAction$` carries the event's description map and
 * every ticket type's maps together, because a partial land — the description stored
 * and one type's write refused — is exactly the "reads as complete while it is
 * short one language" staleness the whole design exists to prevent.
 */
import type { LocalizedText } from "@momo/i18n";
import type { LocalizeCopy } from "@momo/types";
import { Subject } from "rxjs";

export type LocalizeCopyAction = {
	readonly requestId: string;
} & Omit<LocalizeCopy.LocalizeCopyInput, "orgId">;
export const localizeCopyAction$ = new Subject<LocalizeCopyAction>();

/**
 * The failure side carries a reason token, never the callable's own message:
 * the handler answers rate-limiting and model failure with opaque tokens
 * (`too-many-copy-localize-requests`), which is right for a log and wrong for
 * an operator — the panel picks the sentence. Mirrors the artwork channel's
 * own `ArtworkGenerateResult` in this same domain.
 */
export type LocalizeCopyResult =
	| {
			readonly requestId: string;
			readonly ok: true;
			readonly output: LocalizeCopy.LocalizeCopyOutput;
	  }
	| {
			readonly requestId: string;
			readonly ok: false;
			readonly reason: "rate-limited" | "unavailable" | "error";
	  };
export const localizeCopyResult$ = new Subject<LocalizeCopyResult>();

/**
 * The accepted draft, exactly as it goes to disk. Each map is the operator's
 * own accepted text — never the raw model output, which they may have
 * corrected pane by pane — and a null is a subject whose source string was
 * blank, so there was nothing to localize and nothing to store.
 *
 */
export type AcceptCopyAction = {
	readonly requestId: string;
	readonly eventId: string;
	readonly event: { readonly descriptionByLocale: LocalizedText } | null;
	readonly ticketTypes: readonly {
		readonly id: string;
		readonly nameByLocale: LocalizedText;
		readonly descriptionByLocale: LocalizedText | null;
	}[];
};
export const acceptCopyAction$ = new Subject<AcceptCopyAction>();

type AcceptCopyResult = {
	readonly requestId: string;
	readonly ok: boolean;
};
export const acceptCopyResult$ = new Subject<AcceptCopyResult>();
